import { proxy } from "valtio";
import { addFiletoStore } from "./fileStore";
import { templateDefaultValue } from "@constants/template";

interface FileData {
    fileObject?: File;
    Url: string;
    Name: string;
    Type: string;
    Extenxion: string;
}

interface MediaFile extends FileData {}

interface PubItem {
    Pub: MediaFile;
    Rank: number;
    Redirection: string;
}

interface LanguageContent {
    AL: string;
    EN: string;
    ES: string;
    FR: string;
    IT: string;
}

export interface SocialMedia {
    Url: string;
    Activer: boolean;
}

export interface SocialMediaGroup {
    TikTok: SocialMedia;
    Youtube: SocialMedia;
    Facebook: SocialMedia;
    Snapchat: SocialMedia;
    Instagram: SocialMedia;
}

interface HeaderContent {
    Image: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    Title: LanguageContent;
    Description?: LanguageContent;
}

interface PopupContent {
    Image: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    PhoneNumber: string;
}

interface BoutiqueContent {
    Logo: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    Popup: PopupContent;
}

interface Section {
    Title: LanguageContent;
    Image1?: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    Image2?: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    Image3?: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    Images?: {
        Title: LanguageContent;
        Mobile: PubItem[];
        Desktop: PubItem[];
        Description: LanguageContent;
    };
    Color?: string;
    Image?: MediaFile;
    Description?: LanguageContent;
    Activer?: boolean;
    AppStore?: {
        Url: string;
        Activer: boolean;
    };
    PlayeStore?: {
        Url: string;
        Activer: boolean;
    };
}

interface LoyaltyContent {
    Head: {
        Bannier: MediaFile;
    };
    Solde: {
        Title: LanguageContent;
        Info_1: LanguageContent;
        Info_2: LanguageContent;
        BottomTitle: LanguageContent;
    };
    Avantages: {
        Image: MediaFile;
        Title: LanguageContent;
        Info_1: LanguageContent;
        Info_2: LanguageContent;
    };
    Recompenses: {
        Title: LanguageContent;
        BottomTitle: LanguageContent;
    };
}

interface LandingPageContent {
    Head: {
        Title: LanguageContent;
        Bannier: MediaFile;
    };
    Offres: {
        Pubs: PubItem[];
        Title: LanguageContent;
    };
    Loyalty: {
        Title1: LanguageContent;
        Title2: LanguageContent;
        Bannier: MediaFile;
    };
    BestProducts: {
        Title: LanguageContent;
        Products: any[];
    };
}

interface ChoixBoutiqueContent {
    Description: LanguageContent;
    SocialMedia: SocialMediaGroup;
    ImageDetails: MediaFile;
    LogoBoutique: MediaFile;
    BannierBoutique: MediaFile;
}

interface ImagePublicite {
    Logo: MediaFile;
    Pubs: PubItem[];
}

interface Divers {
    Icone?: MediaFile;
    Langue: {
        AL: boolean;
        EN: boolean;
        ES: boolean;
        FR: boolean;
        IT: boolean;
        Faivorit: string;
    };
    ImagePopup?: {
        Mobile: MediaFile;
        Desktop: MediaFile;
    };
    SocialMedia?: SocialMediaGroup;
    CouleurTitre: string;
    TypeEcriture: string;
    ArrondiBouton: string;
    CouleurPrincipale: string;
}

interface PaymentContent {
    [key: string]: {
        Header: HeaderContent;
    };
}

interface ProfileContent {
    [key: string]: {
        Header: HeaderContent;
    };
}

interface PriseCommandeContent {
    [key: string]: {
        Header: HeaderContent;
    };
}

interface SiteWeb {
    Divers: Divers;
    Payment: PaymentContent;
    Profile: ProfileContent;
    LaindingPage: {
        Activer: boolean;
        Bannier: {
            Title: LanguageContent;
            Mobile: PubItem[];
            Desktop: PubItem[];
            Description: LanguageContent;
        };
        Section1: {
            Title: LanguageContent;
            Image1: {
                Mobile: any;
                Desktop: any;
            };
            Image2: {
                Mobile: any;
                Desktop: any;
            };
            Image3: {
                Mobile: any;
                Desktop: any;
            };
        };
        Section2: {
            Title: LanguageContent;
            Images: {
                Mobile: PubItem[];
                Desktop: PubItem[];
            };
        };
        Section3: Section;
        Section4: Section;
    };
    ChoixBoutique: {
        Header: HeaderContent;
        Boutique: {
            [key: string]: BoutiqueContent;
        };
    };
    PriseCommande: PriseCommandeContent;
}

interface Application {
    Divers: Divers;
    Loyalty: {
        Default: LoyaltyContent;
    };
    LaindingPage: {
        Default: LandingPageContent;
    };
    ChoixBoutique: {
        Default: ChoixBoutiqueContent;
    };
    ImagePublicite: ImagePublicite;
}

interface TemplateState {
    template: {
        SiteWeb: SiteWeb;
        Application: Application;
    };
    isLoading: boolean;
    DateModification: string;
    iuud: string;
}

export const templateStore = proxy<TemplateState>({
    template: {
        SiteWeb: {
            Divers: {
                Langue: {
                    AL: false,
                    EN: false,
                    ES: false,
                    FR: true,
                    IT: false,
                    Faivorit: "FR",
                },
                CouleurTitre: "#000000",
                TypeEcriture: "",
                ArrondiBouton: "",
                CouleurPrincipale: "#000000",
                ImagePopup: {
                    Mobile: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },
                    Desktop: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },
                },
            },
            Payment: {
                Default: {
                    Header: {
                        Image: {
                            Mobile: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                            Desktop: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                        },
                        ...templateDefaultValue.SiteWeb.Payment.Default.Header,
                    },
                },
            },
            Profile: {
                Default: {
                    Header: {
                        Image: {
                            Mobile: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                            Desktop: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                        },
                        ...templateDefaultValue.SiteWeb.Profile.Default.Header,
                    },
                },
            },
            LaindingPage: {
                Activer: false,
                Bannier: {
                    ...templateDefaultValue.SiteWeb.LaindingPage.Bannier,
                    Mobile: [],
                    Desktop: [],
                },
                Section1: {
                    ...templateDefaultValue.SiteWeb.LaindingPage.Section1,
                    Image1: {
                        Mobile: {},
                        Desktop: {},
                    },
                    Image2: {
                        Mobile: {},
                        Desktop: {},
                    },
                    Image3: {
                        Mobile: {},
                        Desktop: {},
                    },
                },
                Section2: {
                    ...templateDefaultValue.SiteWeb.LaindingPage.Section2,
                    Images: { Desktop: [], Mobile: [] },
                },
                Section3: {
                    Color: "",
                    ...templateDefaultValue.SiteWeb.LaindingPage.Section3,
                    Image: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },
                },
                Section4: {
                    Color: "",
                    ...templateDefaultValue.SiteWeb.LaindingPage.Section4,
                    Image: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },

                    Activer: true,
                    AppStore: {
                        Url: "",
                        Activer: true,
                    },
                    PlayeStore: {
                        Url: "",
                        Activer: true,
                    },
                },
            },
            ChoixBoutique: {
                Header: {
                    Image: {
                        Mobile: { Url: "", Name: "", Type: "", Extenxion: "" },
                        Desktop: { Url: "", Name: "", Type: "", Extenxion: "" },
                    },
                    ...templateDefaultValue.SiteWeb.ChoixBoutique.Header,
                },
                Boutique: {
                    Default: {
                        Logo: {
                            Mobile: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                            Desktop: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                        },
                        Popup: {
                            Image: {
                                Mobile: {
                                    Url: "",
                                    Name: "",
                                    Type: "",
                                    Extenxion: "",
                                },
                                Desktop: {
                                    Url: "",
                                    Name: "",
                                    Type: "",
                                    Extenxion: "",
                                },
                            },
                            PhoneNumber: "",
                        },
                    },
                },
            },
            PriseCommande: {
                Default: {
                    Header: {
                        Image: {
                            Mobile: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                            Desktop: {
                                Url: "",
                                Name: "",
                                Type: "",
                                Extenxion: "",
                            },
                        },
                        ...templateDefaultValue.SiteWeb.PriseCommande.Default
                            .Header,
                    },
                },
            },
        },
        Application: {
            Divers: {
                Langue: {
                    AL: false,
                    EN: false,
                    ES: false,
                    FR: true,
                    IT: false,
                    Faivorit: "FR",
                },
                CouleurTitre: "#000000",
                TypeEcriture: "",
                ArrondiBouton: "",
                CouleurPrincipale: "#000000",
                Icone: { Url: "", Name: "", Type: "", Extenxion: "" },
            },
            Loyalty: {
                Default: {
                    Head: {
                        Bannier: {
                            Url: "",
                            Name: "",
                            Type: "",
                            Extenxion: "",
                        },
                    },
                    Solde: {
                        ...templateDefaultValue.Application.Loyalty.Default
                            .Solde,
                    },
                    Avantages: {
                        Image: {
                            Url: "",
                            Name: "",
                            Type: "",
                            Extenxion: "",
                        },
                        ...templateDefaultValue.Application.Loyalty.Default
                            .Avantages,
                    },
                    Recompenses: {
                        ...templateDefaultValue.Application.Loyalty.Default
                            .Recompenses,
                    },
                },
            },
            LaindingPage: {
                Default: {
                    Head: {
                        ...templateDefaultValue.Application.LaindingPage.Default
                            .Head,
                        Bannier: {
                            Url: "",
                            Name: "",
                            Type: "",
                            Extenxion: "",
                        },
                    },
                    Offres: {
                        Pubs: [],
                        ...templateDefaultValue.Application.LaindingPage.Default
                            .Offres,
                    },
                    Loyalty: {
                        ...templateDefaultValue.Application.LaindingPage.Default
                            .Loyalty,
                        Bannier: {
                            Url: "",
                            Name: "",
                            Type: "",
                            Extenxion: "",
                        },
                    },
                    BestProducts: {
                        ...templateDefaultValue.Application.LaindingPage.Default
                            .BestProducts,
                        Products: [],
                    },
                },
            },
            ChoixBoutique: {
                Default: {
                    ...templateDefaultValue.Application.ChoixBoutique.Default,
                    SocialMedia: {
                        TikTok: {
                            Url: "",
                            Activer: false,
                        },
                        Youtube: {
                            Url: "",
                            Activer: false,
                        },
                        Facebook: {
                            Url: "",
                            Activer: false,
                        },
                        Snapchat: {
                            Url: "",
                            Activer: false,
                        },
                        Instagram: {
                            Url: "",
                            Activer: false,
                        },
                    },
                    ImageDetails: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },
                    LogoBoutique: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },
                    BannierBoutique: {
                        Url: "",
                        Name: "",
                        Type: "",
                        Extenxion: "",
                    },
                },
            },
            ImagePublicite: {
                Logo: { Url: "", Name: "", Type: "", Extenxion: "" },
                Pubs: [],
            },
        },
    },

    isLoading: true,
    iuud: "",
    DateModification: "",
});

export const templateActions = {
    setTemplate: (template: any, DateModification: string, iuud: string) => {
        templateStore.template = template;
        templateStore.DateModification = DateModification;
        templateStore.iuud = iuud;
    },
    setLoading: (loading: boolean) => {
        templateStore.isLoading = loading;
    },

    updateTemplate: (path: string[], value: any) => {
        let current: any = templateStore;
        for (let i = 0; i < path.length - 1; i++) {
            current = current[path[i]];
            if (!current) return;
        }
        current[path[path.length - 1]] = value;
    },
    updateLanguageContent: (
        path: string[],
        lang: keyof LanguageContent,
        value: string
    ) => {
        const target = templateActions.getNestedObject(path);
        if (target && target[lang] !== undefined) {
            target[lang] = value;
        }
    },
    updateText: (path: string[], value: string) => {
        let current: any = templateStore;
        for (let i = 0; i < path.length - 1; i++) {
            if (!current[path[i]]) return;
            current = current[path[i]];
        }
        current[path[path.length - 1]] = value;
    },

    updateMediaFile: (
        path: string[],
        field: keyof MediaFile,
        value: File | string,
        Rank?: number
    ) => {
        const target = templateActions.getNestedObject(path) as
            | Partial<MediaFile>
            | undefined;
        if (!target) {
            console.error("Target not found for path:", path);
            return;
        }

        const pathKey = path.join(".");

        if (field === "fileObject" && value instanceof File) {
            addFiletoStore(pathKey, value);
            target.Name = value.name;
            target.Type = value.type;
            target.Extenxion = value.name.split(".").pop()?.toUpperCase() || "";
            target.Url = URL.createObjectURL(value);
        } else {
            if (Object.hasOwn(target, field)) {
                (target as any)[field] = value;
            }
        }
    },
    //     const target = templateActions.getNestedObject(path);
    //     if (!target) return;

    //     if ("Title" in target) {
    //         templateActions.updateLanguageContent(
    //             [...path, "Title"],
    //             lang,
    //             defaultData.Title[lang]
    //         );
    //     }
    //     if ("Description" in target) {
    //         templateActions.updateLanguageContent(
    //             [...path, "Description"],
    //             lang,
    //             defaultData.Description[lang]
    //         );
    //     }
    // },

    // resetLanguage: () => {
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Title"],
    //         "FR",
    //         defaultData.Title.FR
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Title"],
    //         "EN",
    //         defaultData.Title.EN
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Title"],
    //         "ES",
    //         defaultData.Title.ES
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Title"],
    //         "IT",
    //         defaultData.Title.IT
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Title"],
    //         "AL",
    //         defaultData.Title.AL
    //     );

    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Description"],
    //         "FR",
    //         defaultData.Description.FR
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Description"],
    //         "EN",
    //         defaultData.Description.EN
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Description"],
    //         "ES",
    //         defaultData.Description.ES
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Description"],
    //         "IT",
    //         defaultData.Description.IT
    //     );
    //     templateActions.updateLanguageContent(
    //         ["template", "SiteWeb", "ChoixBoutique", "Header", "Description"],
    //         "AL",
    //         defaultData.Description.AL
    //     );
    // },

    toggleSocialMedia: (
        path: string[],
        platform: keyof SocialMediaGroup,
        active: boolean
    ) => {
        const target = templateActions.getNestedObject(path);
        if (
            target &&
            target[platform] &&
            target[platform].Activer !== undefined
        ) {
            target[platform].Activer = active;
        }
    },
    getNestedObject: (path: string[]): any => {
        let current: any = templateStore;
        for (const key of path) {
            if (current[key] === undefined) {
                return null;
            }
            current = current[key];
        }
        return current;
    },
    ensurePathExists: (path: string[], defaultValue: any) => {
        let current: any = templateStore.template;
        for (let i = 0; i < path.length; i++) {
            const key = path[i];
            if (current[key] === undefined) {
                current[key] = i === path.length - 1 ? defaultValue : {};
            }
            current = current[key];
        }
    },
};
